import { supabase } from '../lib/supabase';

/**
 * Audit Log Service
 * Provides methods for creating and managing audit logs in the Supabase database
 */
class AuditLogService {
  /**
   * Create a new audit log entry
   * @param {Object} params - Audit log parameters
   * @param {string} params.action - Action performed (e.g., 'DOCUMENT_DELETED', 'ORPHANED_DOCUMENT_REMOVED')
   * @param {string} params.resourceType - Type of resource (e.g., 'document', 'folder')
   * @param {string} params.resourceId - UUID of the affected resource
   * @param {Object} params.details - Additional details as JSONB object
   * @param {string} params.userId - Optional user ID (defaults to current user)
   * @returns {Promise<Object>} Created audit log entry
   */
  async createLog({ action, resourceType, resourceId, details = {}, userId = null }) {
    try {
      // Get current user if userId not provided
      let currentUserId = userId;
      if (!currentUserId) {
        const { data: { user } } = await supabase?.auth?.getUser();
        currentUserId = user?.id || null;
      }

      // Prepare audit log entry
      const logEntry = {
        action,
        resource_type: resourceType,
        resource_id: resourceId,
        details,
        user_id: currentUserId,
        // Get client IP address if available (note: in browser, this is limited)
        ip_address: null, // Will be null for client-side calls, can be populated by server
        created_at: new Date()?.toISOString()
      };

      // Insert audit log
      const { data, error } = await supabase
        ?.from('audit_logs')
        ?.insert(logEntry)
        ?.select()
        ?.single();

      if (error) {
        console.error('[AUDIT_LOG] Error creating audit log:', error);
        throw error;
      }

      console.log('[AUDIT_LOG] Audit log created:', {
        action,
        resourceType,
        resourceId
      });

      return data;
    } catch (error) {
      console.error('[AUDIT_LOG] Failed to create audit log:', error);
      // Don't throw - audit logging failures shouldn't break main operations
      return null;
    }
  }

  /**
   * Log document deletion
   */
  async logDocumentDeletion(documentId, documentName, details = {}) {
    return await this.createLog({
      action: 'DOCUMENT_DELETED',
      resourceType: 'document',
      resourceId: documentId,
      details: {
        document_name: documentName,
        ...details
      }
    });
  }

  /**
   * Log orphaned document removal
   */
  async logOrphanedDocumentRemoval(documentId, documentName, sharePointItemId, reason = 'Not found in SharePoint') {
    return await this.createLog({
      action: 'ORPHANED_DOCUMENT_REMOVED',
      resourceType: 'document',
      resourceId: documentId,
      details: {
        document_name: documentName,
        sharepoint_item_id: sharePointItemId,
        reason,
        timestamp: new Date()?.toISOString(),
        automatic_cleanup: true
      }
    });
  }

  /**
   * Get audit logs for a specific resource
   */
  async getResourceLogs(resourceId, resourceType = 'document') {
    try {
      const { data, error } = await supabase
        ?.from('audit_logs')
        ?.select(`
          *,
          user:user_profiles!user_id(full_name, email)
        `)
        ?.eq('resource_id', resourceId)
        ?.eq('resource_type', resourceType)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('[AUDIT_LOG] Error fetching resource logs:', error);
      return [];
    }
  }

  /**
   * Get recent audit logs with optional filtering
   */
  async getRecentLogs(limit = 50, filters = {}) {
    try {
      let query = supabase
        ?.from('audit_logs')
        ?.select(`
          *,
          user:user_profiles!user_id(full_name, email)
        `);

      // Apply filters
      if (filters?.action) {
        query = query?.eq('action', filters?.action);
      }
      if (filters?.resourceType) {
        query = query?.eq('resource_type', filters?.resourceType);
      }
      if (filters?.userId) {
        query = query?.eq('user_id', filters?.userId);
      }

      const { data, error } = await query
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('[AUDIT_LOG] Error fetching recent logs:', error);
      return [];
    }
  }
}

export default new AuditLogService();